import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { useToast } from '../../hooks/use-toast';

const KYCManagement = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    user_type: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20
  });

  useEffect(() => {
    fetchKYCSubmissions();
    fetchKYCStats();
  }, [filters, pagination]);

  const fetchKYCSubmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip,
        limit: pagination.limit,
        ...(filters.status && { status_filter: filters.status }),
        ...(filters.user_type && { user_type_filter: filters.user_type })
      });

      const response = await api.get(`/kyc/admin/submissions?${params}`);
      setSubmissions(response.data.data.submissions);
    } catch (error) {
      console.error('Error fetching KYC submissions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch KYC submissions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchKYCStats = async () => {
    try {
      const response = await api.get('/kyc/admin/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching KYC stats:', error);
    }
  };

  const handleViewDetails = async (submissionId) => {
    try {
      const response = await api.get(`/kyc/admin/submission/${submissionId}`);
      setSelectedSubmission(response.data.data);
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast({
        title: "Error",
        description: "Failed to fetch submission details",
        variant: "destructive",
      });
    }
  };

  const handleStatusUpdate = async (submissionId, status, rejectionReason = '') => {
    if (status === 'rejected' && !rejectionReason) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a rejection reason",
        variant: "destructive",
      });
      return;
    }

    try {
      await api.put(`/kyc/admin/submission/${submissionId}/status`, {
        status,
        rejection_reason: rejectionReason || null,
        admin_notes: ''
      });

      toast({
        title: "Success",
        description: `KYC ${status} successfully`,
      });
      fetchKYCSubmissions();
      fetchKYCStats();
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error updating KYC status:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to update KYC status',
        variant: "destructive",
      });
    }
  };

  const handleRequestReupload = async (submissionId, reason, documents) => {
    try {
      // Map document field names to enum values
      // Remove "_url" suffix from field names to get the enum value
      const documentTypes = documents.map(doc => doc.replace('_url', ''));
      
      await api.post(`/kyc/admin/submission/${submissionId}/request-reupload`, {
        reason,
        required_documents: documentTypes
      });

      toast({
        title: "Success",
        description: "Re-upload request sent to user",
      });
      fetchKYCSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error requesting reupload:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send re-upload request",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      not_submitted: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Not Submitted' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      under_review: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Under Review' },
      verified: { bg: 'bg-green-100', text: 'text-green-800', label: 'Verified' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      reupload_requested: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Reupload Requested' }
    };

    const config = configs[status] || configs.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">KYC Management</h1>
        <p className="text-gray-600">Review and verify customer KYC submissions</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <StatCard label="Total" value={stats.total_submissions} color="blue" />
          <StatCard label="Pending" value={stats.pending} color="yellow" />
          <StatCard label="Under Review" value={stats.under_review} color="blue" />
          <StatCard label="Verified" value={stats.verified} color="green" />
          <StatCard label="Rejected" value={stats.rejected} color="red" />
          <StatCard label="Reupload" value={stats.reupload_requested} color="orange" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full p-2 border rounded"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
              <option value="reupload_requested">Reupload Requested</option>
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">User Type</label>
            <select
              value={filters.user_type}
              onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
              className="w-full p-2 border rounded"
            >
              <option value="">All Types</option>
              <option value="individual">Individual (B2C)</option>
              <option value="business">Business (B2B)</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', user_type: '' })}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No KYC submissions found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {submission.user_info?.first_name} {submission.user_info?.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{submission.user_info?.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {submission.user_type === 'individual' ? '👤 Individual' : '🏢 Business'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {submission.user_info?.phone}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(submission.status)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(submission.submitted_at)}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleViewDetails(submission.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      Review →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <KYCDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onApprove={(id) => handleStatusUpdate(id, 'verified')}
          onReject={(id, reason) => handleStatusUpdate(id, 'rejected', reason)}
          onRequestReupload={handleRequestReupload}
          onRefresh={() => handleViewDetails(selectedSubmission.id)}
        />
      )}
    </div>
  );
};

// Stats Card Component
const StatCard = ({ label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200'
  };

  return (
    <div className={`p-4 rounded-lg border-2 ${colorClasses[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm font-medium">{label}</div>
    </div>
  );
};

const INDIVIDUAL_SLOTS = [
  ['pan_card', 'PAN Card'],
  ['aadhar_front', 'Aadhaar Front'],
  ['aadhar_back', 'Aadhaar Back'],
  ['selfie', 'Selfie'],
  ['address_proof', 'Address Proof'],
];
const BUSINESS_SLOTS = [
  ['gst_certificate', 'GST Certificate'],
  ['company_pan', 'Company PAN'],
  ['incorporation_certificate', 'Incorporation Certificate'],
  ['cancelled_cheque', 'Cancelled Cheque'],
  ['authorized_signatory_id', 'Authorized Signatory ID'],
];

// Used only when the backend has not sent document_files. We cannot know from
// the browser whether a file is still on disk, so nothing is claimed about it.
const fallbackSlots = (isIndividual, docs) =>
  (isIndividual ? INDIVIDUAL_SLOTS : BUSINESS_SLOTS).map(([type, label]) => {
    const url = (docs || {})[`${type}_url`] || null;
    return { document_type: type, label, url, uploaded: !!url, file_present: !!url };
  });

// KYC Detail Modal Component
const KYCDetailModal = ({ submission, onClose, onApprove, onReject, onRequestReupload, onRefresh }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showReuploadForm, setShowReuploadForm] = useState(false);
  const [reuploadReason, setReuploadReason] = useState('');
  const [selectedDocs, setSelectedDocs] = useState([]);

  const isIndividual = submission.user_type === 'individual';
  const data = isIndividual ? submission.individual_data : submission.business_data;
  const docs = isIndividual ? submission.individual_documents : submission.business_documents;

  // The backend reports every slot for this account type, including ones whose
  // file has gone missing from disk. Older backends do not send it, so fall
  // back to whatever URLs the submission itself carries.
  const documentSlots = submission.document_files || fallbackSlots(isIndividual, docs);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">KYC Submission Review</h2>
            <p className="text-gray-600">
              {submission.user_info?.first_name} {submission.user_info?.last_name} - {isIndividual ? 'Individual' : 'Business'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">User Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{submission.user_info?.email}</span>
              </div>
              <div>
                <span className="text-gray-600">Phone:</span>
                <span className="ml-2 font-medium">{submission.user_info?.phone}</span>
              </div>
              <div>
                <span className="text-gray-600">Submitted:</span>
                <span className="ml-2 font-medium">{formatDateTime(submission.submitted_at)}</span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className="ml-2 font-medium">{submission.status}</span>
              </div>
            </div>
          </div>

          {/* KYC Data */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold mb-3">
              {isIndividual ? 'Personal Information' : 'Business Information'}
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {isIndividual ? (
                <>
                  <InfoField label="Full Name" value={data?.full_name} />
                  <InfoField label="Date of Birth" value={data?.date_of_birth} />
                  <InfoField label="Mobile" value={data?.mobile_number} />
                  <InfoField label="Email" value={data?.email} />
                  <InfoField label="PAN Number" value={data?.pan_number} />
                  <InfoField label="Aadhaar Number" value={data?.aadhar_number || 'Not provided'} />
                  <div className="col-span-2">
                    <InfoField 
                      label="Address" 
                      value={`${data?.address?.street}, ${data?.address?.city}, ${data?.address?.state} - ${data?.address?.postal_code}`} 
                    />
                  </div>
                </>
              ) : (
                <>
                  <InfoField label="Company Name" value={data?.company_name} />
                  <InfoField label="GST Number" value={data?.gst_number} />
                  <InfoField label="PAN Number" value={data?.pan_number} />
                  <InfoField label="CIN Number" value={data?.cin_number || 'Not provided'} />
                  <InfoField label="Authorized Person" value={data?.authorized_person_name} />
                  <InfoField label="Person Mobile" value={data?.authorized_person_mobile} />
                  <InfoField label="Bank Account" value={data?.bank_account_number || 'Not provided'} />
                  <InfoField label="IFSC Code" value={data?.bank_ifsc_code || 'Not provided'} />
                  <div className="col-span-2">
                    <InfoField 
                      label="Registered Address" 
                      value={`${data?.registered_address?.street}, ${data?.registered_address?.city}, ${data?.registered_address?.state} - ${data?.registered_address?.postal_code}`} 
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-semibold mb-3">Uploaded Documents</h3>
            <div className="grid grid-cols-2 gap-4">
              {documentSlots.map((slot) => (
                <DocumentPreview
                  key={slot.document_type}
                  label={slot.label}
                  url={slot.url}
                  slot={slot}
                  submissionId={submission.id}
                  onUploaded={onRefresh}
                />
              ))}
            </div>
          </div>

          {/* Admin Actions */}
          {submission.status !== 'verified' && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3">Admin Actions</h3>
              
              {!showRejectForm && !showReuploadForm && (
                <div className="flex gap-3">
                  <button
                    onClick={() => onApprove(submission.id)}
                    className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700"
                  >
                    ✓ Approve KYC
                  </button>
                  <button
                    onClick={() => setShowRejectForm(true)}
                    className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700"
                  >
                    ✗ Reject KYC
                  </button>
                  <button
                    onClick={() => setShowReuploadForm(true)}
                    className="flex-1 bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-700"
                  >
                    ↻ Request Reupload
                  </button>
                </div>
              )}

              {showRejectForm && (
                <div className="space-y-3">
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter rejection reason (required)"
                    className="w-full p-3 border rounded-lg"
                    rows="3"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        onReject(submission.id, rejectionReason);
                        setShowRejectForm(false);
                      }}
                      disabled={!rejectionReason}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400"
                    >
                      Confirm Rejection
                    </button>
                    <button
                      onClick={() => setShowRejectForm(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {showReuploadForm && (
                <div className="space-y-3">
                  <textarea
                    value={reuploadReason}
                    onChange={(e) => setReuploadReason(e.target.value)}
                    placeholder="Enter reason for reupload request"
                    className="w-full p-3 border rounded-lg"
                    rows="2"
                  />
                  <div className="text-sm">
                    <p className="font-medium mb-2">Select documents to reupload:</p>
                    {Object.keys(docs || {}).map((key) => (
                      <label key={key} className="flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(key)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDocs([...selectedDocs, key]);
                            } else {
                              setSelectedDocs(selectedDocs.filter(d => d !== key));
                            }
                          }}
                        />
                        <span>{key.replace(/_/g, ' ').replace('url', '').trim()}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        onRequestReupload(submission.id, reuploadReason, selectedDocs);
                        setShowReuploadForm(false);
                      }}
                      disabled={!reuploadReason || selectedDocs.length === 0}
                      className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-orange-700 disabled:bg-gray-400"
                    >
                      Send Reupload Request
                    </button>
                    <button
                      onClick={() => setShowReuploadForm(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <span className="text-gray-600 text-xs">{label}:</span>
    <div className="font-medium">{value}</div>
  </div>
);

// Document Preview Component with Modal Viewer
const DocumentPreview = ({ label, url, slot, submissionId, onUploaded }) => {
  const { toast } = useToast();
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [showViewer, setShowViewer] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  
  // Documents are no longer public, so every request has to carry the admin's
  // token: fetch through the API client and hand out a blob URL. Fetched on
  // demand rather than on mount - these are identity documents, and opening a
  // submission should not pull every one of them down automatically.
  const [blobUrl, setBlobUrl] = React.useState(null);
  const blobRef = React.useRef(null);

  React.useEffect(() => () => {
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
  }, []);

  const ensureBlob = async () => {
    if (blobRef.current) return blobRef.current;
    // /uploads/kyc/<owner>/<file>  ->  /kyc/document/<owner>/<file>
    const parts = String(url || '').split('/').filter(Boolean);
    const file = parts[parts.length - 1];
    const owner = parts[parts.length - 2];
    if (!file || !owner) {
      setError(true);
      return null;
    }
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/kyc/document/${owner}/${file}`, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(res.data);
      blobRef.current = objectUrl;
      setBlobUrl(objectUrl);
      setLoading(false);
      return objectUrl;
    } catch (e) {
      setLoading(false);
      setError(true);
      return null;
    }
  };

  const fullUrl = blobUrl;

  // An admin can fill one slot on the customer's behalf: a corrected file
  // arrives by email, or the stored one has gone missing. Making someone redo
  // an entire KYC to fix a single document is not a reasonable ask.
  const handleFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !slot || !submissionId) return;

    const form = new FormData();
    form.append('file', file);
    form.append('document_type', slot.document_type);

    setUploading(true);
    try {
      await api.post(`/kyc/admin/submission/${submissionId}/document`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Drop the cached blob, or the viewer would keep showing the old file.
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
        setBlobUrl(null);
      }
      setError(false);
      toast({ title: `${label} uploaded` });
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: (err && err.response && err.response.data && err.response.data.detail)
          || 'Could not save the document. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };
  
  const handleOpen = async () => {
    setShowViewer(true);
    await ensureBlob();
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  // For direct viewing, open in new tab instead of iframe (more reliable for PDFs)
  const handleViewInNewTab = async () => {
    // window.open(null) navigates the current tab to /admin/null, which is how
    // this used to blank the admin panel.
    const objectUrl = await ensureBlob();
    if (objectUrl) {
      window.open(objectUrl, '_blank', 'noopener');
    } else {
      setShowViewer(true);
    }
  };

  return (
    <>
      <div className="border rounded-lg p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-sm font-medium capitalize">{label}</p>
          {slot && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                slot.file_present
                  ? 'bg-green-100 text-green-700'
                  : slot.uploaded
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-gray-100 text-gray-600'
              }`}
            >
              {slot.file_present ? 'Available' : slot.uploaded ? 'File missing' : 'Not uploaded'}
            </span>
          )}
        </div>

        {slot && slot.uploaded && !slot.file_present && (
          <p className="text-xs text-amber-800 mb-2">
            Recorded on the submission, but the file is no longer on the server.
            Upload a replacement to restore it.
          </p>
        )}

        <div className="flex gap-4 items-center">
          {(!slot || slot.file_present) && url && (
            <button
              onClick={handleViewInNewTab}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View Document →
            </button>
          )}
          {slot && submissionId && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
                onChange={handleFileChosen}
              />
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploading}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : slot.uploaded ? 'Replace' : 'Upload'}
              </button>
            </>
          )}
        </div>
      </div>

      {showViewer && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-semibold text-lg">{label}</h3>
              <button
                onClick={() => setShowViewer(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden relative bg-gray-100">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading document...</p>
                  </div>
                </div>
              )}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-6">
                    <p className="text-red-600 mb-4">Failed to load document in viewer</p>
                    <button
                      onClick={handleViewInNewTab}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-block"
                    >
                      Open in New Tab
                    </button>
                  </div>
                </div>
              )}
              {fullUrl && (
              <iframe
                src={fullUrl}
                className="w-full h-full"
                title={label}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                style={{ display: error ? 'none' : 'block' }}
              />
              )}
            </div>
            <div className="p-4 border-t flex justify-between">
              <button
                onClick={async () => {
                  const objectUrl = await ensureBlob();
                  if (!objectUrl) return;
                  const a = document.createElement('a');
                  a.href = objectUrl;
                  a.download = String(url || 'document').split('/').pop();
                  a.click();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Download
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleViewInNewTab}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Open in New Tab
                </button>
                <button
                  onClick={() => setShowViewer(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KYCManagement;
